import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * MultiUploadToast — sonner custom toast body rendered during and after a
 * multi-file drop. Pure presentational: the drop zone owns the upload state
 * and the preview URLs, this component just paints them.
 *
 * Layout sits inside the shared `.viewer-toast` pill (see `app.scss`) — we
 * only add the inner two-column row (thumbnail stack + status title) and
 * lean on the pill's chrome (white surface, amber border/halo, 14px radius,
 * 48px min-height) for everything else.
 */
import React from "react";
import "./multi-upload-toast.scss";
// Upper bound on how many thumbnail chips we render inline. The stack
// uses -10px overlap on every non-first chip, so each chip past the first
// only consumes 14px of horizontal space — meaning a `+N` overflow chip
// costs the same as showing one more real thumbnail. We only collapse
// into `+N` when the batch genuinely exceeds the inline cap, otherwise
// the last chip would be hidden behind a counter for no reason.
const MAX_PREVIEWS = 4;
const VideoThumbIcon = () => (_jsx("svg", { className: "multi-upload-toast__preview-icon", viewBox: "0 0 24 24", width: "12", height: "12", "aria-hidden": "true", focusable: "false", children: _jsx("path", { d: "M8 5.5v13l11-6.5-11-6.5z", fill: "currentColor" }) }));
/**
 * Renders a small clockwise-filling donut that communicates batch progress.
 *
 * Two concentric stroked circles: a faint track that always shows (so 0%
 * still reads as a placeholder) and a progress ring whose visible arc is
 * controlled via `stroke-dasharray` + `stroke-dashoffset`. Rotating the
 * ring -90° anchors the fill to 12 o'clock and makes it grow clockwise.
 */
const ProgressRing = ({ progress }) => {
    const size = 14;
    const strokeWidth = 2;
    const cx = size / 2;
    const cy = size / 2;
    const r = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * r;
    const clamped = Math.min(1, Math.max(0, progress));
    const offset = circumference * (1 - clamped);
    return (_jsxs("svg", { className: "multi-upload-toast__ring", width: size, height: size, viewBox: `0 0 ${size} ${size}`, "aria-hidden": "true", focusable: "false", children: [_jsx("circle", { className: "multi-upload-toast__ring-track", cx: cx, cy: cy, r: r }), _jsx("circle", { className: "multi-upload-toast__ring-progress", cx: cx, cy: cy, r: r, strokeDasharray: circumference, strokeDashoffset: offset })] }));
};
const MultiUploadPreview = ({ upload, url }) => {
    const isImage = upload.file.type.startsWith("image/");
    return (_jsx("div", { className: `multi-upload-toast__preview multi-upload-toast__preview--${upload.status}`, "aria-hidden": "true", children: isImage && url !== undefined ? (_jsx("img", { src: url, alt: "", className: "multi-upload-toast__preview-img" })) : (_jsx(VideoThumbIcon, {})) }));
};
export const MultiUploadToastBody = ({ uploads, previewUrls, isUploading, }) => {
    const total = uploads.length;
    const succeeded = uploads.filter((u) => u.status === "success").length;
    const failed = uploads.filter((u) => u.status === "error").length;
    const title = (() => {
        if (isUploading) {
            return total === 1 ? "Uploading 1 file…" : `Uploading ${total} files…`;
        }
        if (failed === 0) {
            return total === 1 ? "Added 1 file" : `Added ${total} files`;
        }
        if (succeeded === 0) {
            return total === 1 ? "Upload failed" : `${total} failed`;
        }
        return `${succeeded} of ${total} added`;
    })();
    const previews = uploads.slice(0, MAX_PREVIEWS);
    const overflow = total - previews.length;
    // Group-level progress: how many files have reached a terminal status
    // (success or error) out of the total in the batch. We count failures as
    // "done" because the pie tracks "has this slot finished moving through
    // the pipeline", not "has this slot succeeded".
    const settled = succeeded + failed;
    const progress = total === 0 ? 0 : settled / total;
    return (_jsxs("div", { className: "multi-upload-toast", children: [_jsxs("div", { className: "multi-upload-toast__stack", "aria-hidden": "true", children: [previews.map((u) => (_jsx(MultiUploadPreview, { upload: u, url: previewUrls.get(u.id) }, u.id))), overflow > 0 ? (_jsxs("div", { className: "multi-upload-toast__preview multi-upload-toast__preview--overflow", children: ["+", overflow] })) : undefined] }), _jsx("span", { className: "multi-upload-toast__title", children: title }), isUploading ? _jsx(ProgressRing, { progress: progress }) : undefined] }));
};
